import toast from 'react-hot-toast';

import { registerPlugin, FilePond } from 'react-filepond';
import FilePondPluginFileValidateSize from 'filepond-plugin-file-validate-size';
import FilePondPluginFileEncode from 'filepond-plugin-file-encode';
import FilePondPluginFileValidateType from 'filepond-plugin-file-validate-type';

import MediaPreview from './MediaPreview';

registerPlugin(FilePondPluginFileEncode, FilePondPluginFileValidateSize, FilePondPluginFileValidateType);

export default function MediaUploader({
  mediaUrl,
  onSetSavedMedia,
  onSetMedia,
  onDeleteMedia,
  acceptedFileTypes = ['image/png', 'image/jpeg', 'image/svg+xml'],
  labelFileTypeNotAllowed = 'Only .svg, .png, .jpg and .jpeg files are allowed',
  fileValidateTypeLabelExpectedTypes = 'Allowed file types: {allTypes}',
  maxFileSize = '1MB',
  labelMaxFileSize = 'File should not be larger than 1MB',
}) {
  if (mediaUrl) {
    const files = mediaUrl
      ? [
          {
            source: mediaUrl,
            options: {
              type: 'local',
            },
          },
        ]
      : [];

    return (
      <div className="d-flex flex-column gap-3">
        <MediaPreview srcUrl={mediaUrl} />
        <FilePond
          id={`site-logo-input-${crypto.randomUUID()}`}
          files={files}
          acceptedFileTypes={acceptedFileTypes}
          labelFileTypeNotAllowed={labelFileTypeNotAllowed}
          fileValidateTypeLabelExpectedTypes={fileValidateTypeLabelExpectedTypes}
          maxFileSize={maxFileSize}
          labelMaxFileSize={labelMaxFileSize}
          instantUpload={false}
          labelButtonRemoveItem="Remove"
          labelIdle='Drag & Drop or <span class="filepond--label-action">Browse</span>'
          onwarning={(error, file, status) => {
            if (error.body === 'MAX_FILE_SIZE_EXCEEDED') {
              toast.error('The file is too large. Please upload a file smaller than 1MB.');
            }
          }}
          onremovefile={() => {
            onSetSavedMedia('');
            onDeleteMedia(true);
          }}
          labelFileLoadError="Unable to display the uploaded media file"
          labelButtonRetryItemLoad="Click to retry"
          server={{
            load: (source, load, error, _, abort) => {
              try {
                const url = new URL(source);
                const urlParams = new URLSearchParams(url.search);

                const rawFilename = urlParams.get('filename') || 'Unknown file name';
                const rawContentType = urlParams.get('content-type') || '';

                const filename = decodeURIComponent(rawFilename);
                const contentType = decodeURIComponent(rawContentType);

                fetch(source)
                  .then((response) => {
                    if (!response.ok) {
                      error('Could not load image');
                      return;
                    }
                    return response.blob().then((blob) => {
                      const file = new File([blob], filename, { type: contentType || blob.type });
                      load(file);
                    });
                  })
                  .catch(() => {
                    error('Could not load image');
                  });
                return {
                  abort: () => abort(),
                };
              } catch (e) {
                error('Could not load image');
                return {
                  abort: () => abort(),
                };
              }
            },
          }}
          maxFiles={1}
          credits={false}
        />
      </div>
    );
  }

  return (
    <FilePond
      id={`media-uploader-input-${crypto.randomUUID()}`}
      acceptedFileTypes={acceptedFileTypes}
      labelFileTypeNotAllowed={labelFileTypeNotAllowed}
      fileValidateTypeLabelExpectedTypes={fileValidateTypeLabelExpectedTypes}
      onaddfile={(error, file) => {
        if (error) {
          console.error('Filepond error:', error);
          return;
        }

        const base64Data = file.getFileEncodeBase64String();

        if (file.filename && base64Data) {
          onSetMedia({
            filename: file.filename,
            base64_file_data: base64Data,
          });
        } else {
          console.error(`Filepond error: File data or filename is missing ${file.filename} || file data: ${base64Data}`);
        }
      }}
      onremovefile={() => {
        onSetMedia({
          filename: '',
          base64_file_data: '',
        });
      }}
      maxFiles={1}
      credits={false}
      labelIdle='Drag & Drop your file or <span class="filepond--label-action">Browse</span>'
      maxFileSize={maxFileSize}
      labelMaxFileSize={labelMaxFileSize}
    />
  );
}
